import moment from "moment";

import {DaysOfWeek} from "@uw-it-sis/lib-react/lib/AppConstants";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";

/**
 * Converts a string of 12 hour time (e.g. "3:00 PM") into 24 hour time (e.g. "15:00")
 * for usage in fullcalendar events.
 * @param time
 * @return {string}
 */
export function convert24hour(time) {
    return moment(time.trim(), 'h:mm A').format('HH:mm')
}

/**
 * Take a class meeting detail and builds an object for usage in fullcalendar events.
 * @param meeting - an object containing props for days of the week as bool, along
 * with start and end times.
 * @return {{}}
 */
export function meetingToEvent(meeting) {
    let tempEvent = {};

    let hasDaysOfWeek = DaysOfWeek.some(day => meeting[day.key]);

    if (hasDaysOfWeek) {
        tempEvent.daysOfWeek = DaysOfWeek.filter(day => meeting[day.key]).map(day => day.index);
        tempEvent.startTime = convert24hour(meeting.startTime);
        tempEvent.endTime = convert24hour(meeting.endTime);
    } else {
        tempEvent.allDay = true;
    }

    return tempEvent;
}

/**
 * Builds a list of events for usage in fullcalendar based on a schedule option activity options.
 * @param options - a list of activity options
 * @param startDate - the first day of the week for displaying fullcalendar events on schedule builder.
 * @return {[]}
 */
export function buildEvents(options, startDate, indicator) {
    let events = [];

    // build props for the start and end of the week to display in schedule builder's fullcalendar
    const eventDates = {
        startRecur: startDate,
        endRecur: moment(startDate, 'YYYY-MM-DD').add(7, 'days').format('YYYY-MM-DD')
    };

    //
    options.map(course => {
        const isSummer = course.hasOwnProperty("summerSubTerm") && TermUtils.makeTermName(course.termId) === TermUtils.TERMS["3"];
        course.classMeetingTimes.map(meeting => {
            let tempEvent = Object.assign({}, {
                title: `${course.courseCd} ${course.activityCode}`,
                //Custom properties needed to build the course modal on click event
                extendedProps: {
                    indicator: indicator,
                    courseObj: {...course},
                    registered: false,
                    badges: []
                }
            }, eventDates, meetingToEvent(meeting));

            if (course.blockParentCourseCode && course.blockParentSectionCode && course.blockRegistrationType) {
                tempEvent.extendedProps.badges.push(`${course.blockRegistrationType} ${course.blockParentSectionCode}`)
            }

            if (isSummer) {
                tempEvent.extendedProps.badges.push(course.summerSubTerm !== "" ? course.summerSubTerm : "full-term");
            }

            events.push(tempEvent);
        });

        course.secondaryOptions.map(activityOptionsInfo => {
            activityOptionsInfo.activityOptions.map(activity => {
                activity.classMeetingTimes.map(meeting => {
                    let tempEvent = Object.assign({}, {
                        title: `${activity.courseCd} ${activity.activityCode}`,
                        //Custom properties needed to build the course modal on click event
                        extendedProps: {
                            indicator: indicator,
                            courseObj: {...course},
                            registered: false,
                            badges: []
                        }
                    }, eventDates, meetingToEvent(meeting));

                    if (activity.blockParentCourseCode && activity.blockParentSectionCode && activity.blockRegistrationType) {
                        tempEvent.extendedProps.badges.push(`${activity.blockRegistrationType} ${activity.blockParentSectionCode}`)
                    }

                    if (isSummer) {
                        tempEvent.extendedProps.badges.push(activity.summerSubTerm !== "" ? activity.summerSubTerm : "full-term");
                    }

                    events.push(tempEvent);
                });
            })
        });

        if (course.hasOwnProperty("dupEnrolledActivities") && course.dupEnrolledActivities.length > 0) {
            course.dupEnrolledActivities.map(activity => {
                activity.classMeetingTimes.map(meeting => {
                    let tempEvent = Object.assign({}, {
                        title: `${activity.courseCd} ${activity.activityCode}`,
                        //Custom properties needed to build the course modal on click event
                        extendedProps: {
                            indicator: indicator,
                            courseObj: {...course},
                            registered: false,
                            badges: []
                        }
                    }, eventDates, meetingToEvent(meeting));

                    if (activity.blockParentCourseCode && activity.blockParentSectionCode && activity.blockRegistrationType) {
                        tempEvent.extendedProps.badges.push(`${activity.blockRegistrationType} ${activity.blockParentSectionCode}`)
                    }

                    if (isSummer) {
                        tempEvent.extendedProps.badges.push(activity.summerSubTerm !== "" ? activity.summerSubTerm : "full-term");
                    }

                    events.push(tempEvent);
                });
            })
        }
    });

    return events;
}

/**
 * Return the list of course and section codes ( i.e. 'COM 220 A', 'COM 220 AZ', 'ENGL 142 B' )
 * from the given schedule option.
 *
 * If any options are ambiguous ( more than one section code exists for the given time slot ), the selectedCodes
 * list is checked to see if it contains a code from the ambiguous set.  If it is found, that code is returned
 * as the member of the result list.
 *
 * If any ambiguous section sets cannot be resolved in this way, an empty array is returned
 *
 * @param schedule the schedule option data
 * @param selectedCodes a list of section codes to consider to resolve ambiguous schedule entries
 * @return a list of section codes, or an empty list if a concrete list cannot be determined
 */
export function getSectionCodesFromScheduleOption(schedule, selectedCodes) {
    let result = [];
    let unresolvedAmbiguousItem = false;

    if (isEmpty(selectedCodes)) {
        selectedCodes = [];
    }

    schedule.activityOptions.map( item => {

        let itemAndSecondaries = [];
        if (!isEmpty(item.secondaryOptions)) {
            item.secondaryOptions.map( secondaryObj => {
                secondaryObj.activityOptions.map( secondaryActivity => {
                    itemAndSecondaries.push(secondaryActivity);
                });
            });
        }
        //Add the duplicate enrolled activities too as item
        if (item.dupEnrolledActivities && !isEmpty(item.dupEnrolledActivities)) {
            item.dupEnrolledActivities.map( duplicateObj => {
                    itemAndSecondaries.push(duplicateObj);
                });

        }
        itemAndSecondaries.push(item);

        itemAndSecondaries.map( item => {

            let itemCode = `${item.courseCd} ${item.activityCode}`;

            if (isEmpty(item.alternateActivities)) {
                result.push(itemCode);
            } else {
                // include the non-alternate code along with the alternates in a list
                // to see if any of those codes are among the selected codes
                let codesForActivityItems = item.alternateActivities.map( x => `${x.courseCd} ${x.activityCode}`);
                codesForActivityItems.push(itemCode);

                let match = codesForActivityItems.find(itemCode => {
                    return selectedCodes.includes(itemCode);
                });

                if (isEmpty(match)) {
                    unresolvedAmbiguousItem = true;
                } else {
                    result.push(match)
                }
            }
        });

    });

    if (unresolvedAmbiguousItem) {
        return [];
    }

    return result;
}

export function timeMaskCountForSchedule(schedule) {
    let timeMaskSectionCount = 0;

    for (const item of schedule.activityOptions) {
        if (hasTimeMaskForActivityOption(item)) {
            timeMaskSectionCount++;
        }
    }

    return timeMaskSectionCount;
}


/**
 * Builds a map of SLN data (SLN being key) with data around if its in a block reg, if its a block
 * reg parent, a prop for an entry code input and whether the entry code is required or not.
 * @param schedule - a schedule option object
 * @param selectedCodes - a list of section codes user has selected for registration
 * @return {{}}
 */
export function buildSlnData(schedule, selectedCodes) {
    let slnData = {};

    schedule.activityOptions.map( activity => {
        let activityCode = `${activity.courseCd} ${activity.activityCode}`;
        if ( selectedCodes.includes(activityCode) ) {
            slnData[activity.registrationCode] = {
                activityCode: activityCode,
                entryCode: "",
                isEntryCodeRequired: activity.addCodeRequired,
                isBlockReg: (activity.blockParentCourseCode && activity.blockParentSectionCode && activity.blockRegistrationType),
                isBlockRegParent: (normalize(activityCode) === normalize(`${activity.blockParentCourseCode} ${activity.blockParentSectionCode}`))
            }
        }

        if ( !isEmpty(activity.alternateActivities) ) {
            activity.alternateActivities.map( alternateActivity => {
                let alternateActivityCode = `${alternateActivity.courseCd} ${alternateActivity.activityCode}`;
                if ( selectedCodes.includes(alternateActivityCode) ) {
                    slnData[alternateActivity.registrationCode] = {
                        activityCode: alternateActivityCode,
                        entryCode: "",
                        isEntryCodeRequired: alternateActivity.addCodeRequired,
                        isBlockReg: (alternateActivity.blockParentCourseCode && alternateActivity.blockParentSectionCode && alternateActivity.blockRegistrationType),
                        isBlockRegParent: (normalize(alternateActivityCode) === normalize(`${alternateActivity.blockParentCourseCode} ${alternateActivity.blockParentSectionCode}`))
                    }
                }
            });
        }

        if ( !isEmpty(activity.dupEnrolledActivities) ) {
            activity.dupEnrolledActivities.map( dupEnrolledActivity => {
                let dupEnrolledActivityCode = `${dupEnrolledActivity.courseCd} ${dupEnrolledActivity.activityCode}`;
                if ( selectedCodes.includes(dupEnrolledActivityCode) ) {
                    slnData[dupEnrolledActivity.registrationCode] = {
                        activityCode: dupEnrolledActivityCode,
                        entryCode: "",
                        isEntryCodeRequired: dupEnrolledActivity.addCodeRequired,
                        isBlockReg: (dupEnrolledActivity.blockParentCourseCode && dupEnrolledActivity.blockParentSectionCode && dupEnrolledActivity.blockRegistrationType),
                        isBlockRegParent: (normalize(dupEnrolledActivityCode) === normalize(`${dupEnrolledActivity.blockParentCourseCode} ${dupEnrolledActivity.blockParentSectionCode}`))
                    }
                }
            });
        }

        if ( !isEmpty(activity.secondaryOptions) ) {
            activity.secondaryOptions.map( secondaryObj => {
                secondaryObj.activityOptions.map( secondaryActivity => {
                    let secondaryActivityCode = `${secondaryActivity.courseCd} ${secondaryActivity.activityCode}`;
                    if ( selectedCodes.includes(secondaryActivityCode) ) {
                        slnData[secondaryActivity.registrationCode] = {
                            activityCode: secondaryActivityCode,
                            entryCode: "",
                            isEntryCodeRequired: secondaryActivity.addCodeRequired,
                            isBlockReg: (secondaryActivity.blockParentCourseCode && secondaryActivity.blockParentSectionCode && secondaryActivity.blockRegistrationType),
                            isBlockRegParent: (normalize(secondaryActivityCode) === normalize(`${secondaryActivity.blockParentCourseCode} ${secondaryActivity.blockParentSectionCode}`))
                        }
                    }

                    if ( !isEmpty(secondaryActivity.alternateActivities) ) {
                        secondaryActivity.alternateActivities.map( secondaryAlternateActivity => {
                            let secondaryAlternateActivityCode = `${secondaryAlternateActivity.courseCd} ${secondaryAlternateActivity.activityCode}`;
                            if ( selectedCodes.includes(secondaryAlternateActivityCode) ) {
                                slnData[secondaryAlternateActivity.registrationCode] = {
                                    activityCode: secondaryAlternateActivityCode,
                                    entryCode: "",
                                    isEntryCodeRequired: secondaryAlternateActivity.addCodeRequired,
                                    isBlockReg: (secondaryAlternateActivity.blockParentCourseCode && secondaryAlternateActivity.blockParentSectionCode && secondaryAlternateActivity.blockRegistrationType),
                                    isBlockRegParent: (normalize(secondaryAlternateActivityCode) === normalize(`${secondaryAlternateActivity.blockParentCourseCode} ${secondaryAlternateActivity.blockParentSectionCode}`))
                                }
                            }
                        });
                    }
                });
            });
        }
    });

    return slnData;
}

export function hasTimeMaskForActivityOption(item) {
    let hasTimeMask = false;

    if (item.timeMaskFlag) {
        return true;
    } else {
        if (item.dupEnrolledActivities) {
            hasTimeMask = item.dupEnrolledActivities.some(activity => activity.timeMaskFlag);
        }
        if (hasTimeMask) {
            return true;
        } else {
            if (item.secondaryOptions) {
                for (const activityOptionsInfo of item.secondaryOptions) {
                    for (const activity of activityOptionsInfo.activityOptions) {
                        hasTimeMask = activity.timeMaskFlag || activity.alternateActivities.some(activity => activity.timeMaskFlag);
                        if (hasTimeMask) {
                            return true;
                        }
                    }
                    if (hasTimeMask) {
                        return true;
                    }
                }
            }
        }
    }

    return hasTimeMask;
}

export function hasDupEnrollForSchedule(schedule) {
    let hasDupEnroll = false;

    for (const item of schedule.activityOptions) {
        hasDupEnroll = item.dupEnrolledActivities.length > 0;
        if (hasDupEnroll) {
            break;
        }
    }

    return hasDupEnroll;
}

/**
 * Takes a list of schedules and determines the earliest start time and the latest end time
 * for all sections in the given list of schedules. Also finds if there are any weekend scheduled
 * sections.
 * @param schedules - a list of schedule objects
 * @return {{slotMaxTime: string, slotMinTime: string, hasWeekends: boolean}}
 */
export function getCalendarProps(schedules) {
    // the pattern for the times provided by class meeting data
    let timePattern = 'h:mm A';
    let startTimes = [];
    let endTimes = [];

    let hasWeekends = false;

    let defaultMinTime = moment("8:00 AM", timePattern);
    let defaultMaxTime = moment("7:00 PM", timePattern);

    // loop the possible schedules drill into each activity option, secondary options,
    // alternate options, and duplicate
    schedules.map(schedule => {
        schedule.activityOptions.map(item => {
            // loop class meeting times on primary
            item.classMeetingTimes.map(meeting => {
                if (meeting.startTime !== meeting.endTime) {
                    if (meeting.startTime) startTimes.push(meeting.startTime);
                    if (meeting.endTime) endTimes.push(meeting.endTime);
                }

                if (!hasWeekends && meeting.saturday) {
                    hasWeekends = true;
                }
            })

            if (item.secondaryOptions.length > 0) {
                item.secondaryOptions.map(activityOptionsInfo => {
                    if (activityOptionsInfo.activityOptions.length > 0) {
                        activityOptionsInfo.activityOptions.map(activity => {
                            activity.classMeetingTimes.map(meeting => {
                                if (meeting.startTime !== meeting.endTime) {
                                    if (meeting.startTime) startTimes.push(meeting.startTime);
                                    if (meeting.endTime) endTimes.push(meeting.endTime);
                                }

                                if (!hasWeekends && meeting.saturday) {
                                    hasWeekends = true;
                                }
                            });

                            if (activity.alternateActivities.length > 0) {
                                activity.alternateActivities.map(alternate => {
                                    alternate.classMeetingTimes.map(meeting => {
                                        if (meeting.startTime !== meeting.endTime) {
                                            if (meeting.startTime) startTimes.push(meeting.startTime);
                                            if (meeting.endTime) endTimes.push(meeting.endTime);
                                        }

                                        if (!hasWeekends && meeting.saturday) {
                                            hasWeekends = true;
                                        }
                                    });
                                });
                            }
                        });
                    }
                });
            }

            if (item.dupEnrolledActivities.length > 0) {
                item.dupEnrolledActivities.map(activity => {
                    activity.classMeetingTimes.map(meeting => {
                        if (meeting.startTime !== meeting.endTime) {
                            if (meeting.startTime) startTimes.push(meeting.startTime);
                            if (meeting.endTime) endTimes.push(meeting.endTime);
                        }

                        if (!hasWeekends && meeting.saturday) {
                            hasWeekends = true;
                        }
                    });
                });
            }
        })
    });

    let minTime;
    let maxTime;

    // Find the earliest start time
    if (startTimes && startTimes.length > 0) {
        minTime = startTimes.reduce((pre, cur) => {
            let acc = moment(pre, timePattern);
            let value = moment(cur, timePattern);
            return value.isBefore(acc) ? value : acc;
        });
    }

    // Find the latest end time
    if (endTimes && endTimes.length > 0) {
        maxTime = endTimes.reduce((pre, cur) => {
            let acc = moment(pre, timePattern);
            let value = moment(cur, timePattern);
            return value.isAfter(acc) ? value : acc;
        });
    }

    // Dont allow min time to be after default (8am)
    if ( minTime && typeof minTime === 'string' ) {
        // Reducer comes back as string if there is only 1 time in the minTime array
        minTime = moment(minTime, timePattern);
    }
    if ( !minTime || minTime.isAfter(defaultMinTime) ) {
        minTime = defaultMinTime;
    }

    // Dont allow max time to be before default (8pm)
    if ( maxTime && typeof maxTime === 'string' ) {
        // Reducer comes back as string if there is only 1 time in the maxTime array
        maxTime = moment(maxTime, timePattern);
    }
    if ( !maxTime || maxTime.isBefore(defaultMaxTime) ) {
        maxTime = defaultMaxTime;
    }

    return {
        slotMinTime: minTime.startOf('hour').format('HH:mm:ss'),
        slotMaxTime: maxTime.endOf('hour').format('HH:mm:ss'),
        hasWeekends: hasWeekends
    }
}

/**
 * Course Sort Functions
 * Used in plan items and schedule options to sort items so they appear in the same order
 * Done in alphabetical order
 */

export function coursePlanItemSort(a, b) {
    const codeA = normalize(a.courseDetails.code);
    const codeB = normalize(b.courseDetails.code);

    if (codeA < codeB) return -1;
    if (codeA > codeB) return 1;
    return 0;
}

export function courseActivityOptionSort(a, b) {
    const codeA = normalize(a.courseCd);
    const codeB = normalize(b.courseCd);

    if (codeA < codeB) return -1;
    if (codeA > codeB) return 1;
    return 0;
}

/**
 * Determine if two activity ids represent the same course code.
 *
 * i.e. true for these inputs: 2021:3:COM:498:A ~= 2021:3:COM:498:B
 * and false for these:   2021:3:BAR:498:A !~= 2021:3:COM:498:B
 *
 * Not null safe, so use with care.
 */
export function isSameCourseCodeForActivityId(id1, id2) {
    let tokens1 = id1.split(":");
    tokens1.splice(4, 1);
    let tokens2 = id2.split(":");
    tokens2.splice(4, 1);
    let courseCode1 = tokens1.join(":");
    let courseCode2 = tokens2.join(":");

    return courseCode1 === courseCode2;
}

/**
 * Prepare a set of activity options and metadata related to displaying them and potential alternate sections.
 *
 * @param activityOptions the set of schedule activity options to examine
 * @param showAlternatesPrimaryActivityIds a set of primary activity ids that have been selected by the user to have their alternates shown
 * @param primaryActivityIdsRequiringAlternateSelection the primary activity ids of any activities that have alternates to select
 * @param selectedAlternateCodes the set of course and section codes matching alternates that have been selected by the user
 * @param showSendToRegistration a flag indicating that the display is one ready to send to registration
 *
 * @return {[{item: object, courseActivities: [], alternatePrimaryList: []}]}
 */
export function prepareScheduleActivityOptions(activityOptions, showAlternatesPrimaryActivityIds, primaryActivityIdsRequiringAlternateSelection, selectedAlternateCodes, showSendToRegistration) {

    let preparedSchedules = [];

    if (!showAlternatesPrimaryActivityIds) {
        showAlternatesPrimaryActivityIds = [];
    }

    if (!primaryActivityIdsRequiringAlternateSelection) {
        primaryActivityIdsRequiringAlternateSelection = [];
    }

    if (!selectedAlternateCodes) {
        selectedAlternateCodes = [];
    }

    activityOptions.sort(courseActivityOptionSort).map((item) => {

        let courseActivities = [];
        let alternatePrimaryList = [];
        let showAlternates = showAlternatesPrimaryActivityIds.includes(item.activityOfferingId);

        if (!showSendToRegistration) {
            courseActivities.push(item);

            // Check if the primary has any alternates
            if (item.primary && showAlternates && primaryActivityIdsRequiringAlternateSelection.includes(item.activityOfferingId) && item.alternateActivities.length > 0) {
                alternatePrimaryList.push(item.activityOfferingId);
                item.alternateActivities.map(altItem => {
                    courseActivities.push(altItem);
                    alternatePrimaryList.push(altItem.activityOfferingId);
                });
            }

            // First level contains the primary section, secondary sections are within the list of secondaryOptions
            item.secondaryOptions.map(activityOptionsInfo => {
                activityOptionsInfo.activityOptions.map(activity => {
                    courseActivities.push(activity);

                    // if this secondary activity should include the set of alternates as form choices, add them
                    if (showAlternates && primaryActivityIdsRequiringAlternateSelection.includes(activity.parentActivityId)) {
                        activity.alternateActivities.map(altItem => {
                            courseActivities.push(altItem);
                        });
                    }
                })
            });

            if (item.hasOwnProperty("dupEnrolledActivities") && item.dupEnrolledActivities.length > 0) {
                courseActivities[0].duplicateEnrollmentAllowed = true;
                item.dupEnrolledActivities.map(activity => {
                    activity.duplicateEnrollmentAllowed = true;
                    courseActivities.push(activity);
                });
            }
        } else {
            // Populate courseActivities with only sections to be set to reg

            // Primary, Alternates Primaries, and Duplicate Enrollment primaries
            if (item.primary && primaryActivityIdsRequiringAlternateSelection.includes(item.activityOfferingId) && item.alternateActivities.length > 0) {
                if (selectedAlternateCodes.includes(`${item.courseCd} ${item.activityCode}`)) {
                    courseActivities.push(item);
                } else {
                    item.alternateActivities.map(altItem => {
                        if (selectedAlternateCodes.includes(`${altItem.courseCd} ${altItem.activityCode}`)) {
                            courseActivities.push(altItem);
                        }
                    });
                }
            } else {
                if (item.hasOwnProperty("dupEnrolledActivities") && item.dupEnrolledActivities.length > 0) {
                    if (selectedAlternateCodes.includes(`${item.courseCd} ${item.activityCode}`)) {
                        courseActivities.push(item);
                    } else {
                        item.dupEnrolledActivities.map(dupItem => {
                            if (selectedAlternateCodes.includes(`${dupItem.courseCd} ${dupItem.activityCode}`)) {
                                courseActivities.push(dupItem);
                            }
                        });
                    }
                } else {
                    courseActivities.push(item);
                }
            }

            // Secondary and Alternates
            if (item.secondaryOptions.length > 0) {
                item.secondaryOptions.map(activityOptionsInfo => {
                    activityOptionsInfo.activityOptions.map(activity => {
                        if (activity.alternateActivities.length > 0 && primaryActivityIdsRequiringAlternateSelection.includes(activity.parentActivityId)) {
                            if (selectedAlternateCodes.includes(`${activity.courseCd} ${activity.activityCode}`)) {
                                courseActivities.push(activity);
                            } else {
                                activity.alternateActivities.map(altItem => {
                                    if (selectedAlternateCodes.includes(`${altItem.courseCd} ${altItem.activityCode}`)) {
                                        courseActivities.push(altItem);
                                    }
                                });
                            }
                        } else {
                            courseActivities.push(activity);
                        }
                    });
                });
            }
        }

        preparedSchedules.push({
            item: item,
            courseActivities: courseActivities,
            alternatePrimaryList: alternatePrimaryList
        });
    });

    return preparedSchedules;
}